/**
 * Cliente de la API.
 *
 * Toda peticion lleva el initData de Telegram en la cabecera. El servidor lo
 * valida con HMAC-SHA256 y el token del bot en CADA peticion: no hay sesiones ni
 * tokens propios que robar.
 *
 * MODO DEV. Fuera de Telegram no hay initData, asi que no hay nada que firmar.
 * En ese caso el cliente no llama al servidor: genera el seed en local y guarda
 * el progreso en localStorage, para poder jugar y afinar el juego sin levantar
 * el backend. El modo DEV se detecta por la AUSENCIA de Telegram, no por una
 * bandera que se pueda quedar encendida por error en produccion.
 */

import { AVATAR_POR_DEFECTO, TRAJES, normalizar, type Avatar } from '../game/avatar';
import { telegram } from './telegram';

export interface InicioPartida {
  runId: string;
  seed: number;
  /** Firma del servidor sobre (runId, seed). Se devuelve al terminar. */
  firma: string;
  /** Si el seed viene de un duelo, su identificador. */
  duelId?: string;
  /**
   * Si esta partida va con monedas dobladas. **Lo concede el servidor**, que es
   * quien sabe si el anuncio se vio: el cliente lo pide, no lo decide.
   */
  x2?: boolean;
  /** Cuantas veces se puede revivir en esta partida. */
  revivirRestantes?: number;
  /** Lo que cuesta revivir pagando. Lo decide el SERVIDOR, no el cliente. */
  precioRevivirMonedas?: number;
}

export interface FinPartida {
  runId: string;
  firma: string;
  distancia: number;
  monedas: number;
  duracionMs: number;
  segmentos: number;
  registroEntradas: string;
}

export interface ResultadoFin {
  aceptada: boolean;
  monedasAcreditadas: number;
  recordNuevo: boolean;
  record: number;
  monedasTotales: number;
  /** Si el servidor rechazo la partida, por que. Para poder avisar con sentido. */
  motivoRechazo?: string;
  /** Veces que revivio, segun el servidor. */
  revivires?: number;
  /**
   * El reto que esta partida acaba de ganar, si lo hubo. Es lo que permite
   * ofrecer doblar el premio en el momento en que se acaba de enterar.
   * Solo lo recibe quien juega SEGUNDO: el reto se resuelve con esa partida.
   */
  dueloGanado?: { duelId: string; premio: number; moneda: string } | null;
}

export interface Perfil {
  record: number;
  monedas: number;
  duelosGanados: number;
  racha: number;
  /** Los cinco indices del avatar. Ver `game/avatar.ts`. */
  avatar: Avatar;
  /** Los requisitos cumplidos: con esto se pintan los candados de los trajes. */
  desbloqueos: string[];
  /** Si alguna vez abrio el creador. Sirve para proponerselo solo la primera vez. */
  avatarElegido: boolean;
  /** El enlace de invitacion, montado por el SERVIDOR (el unico que sabe el nombre del bot). */
  enlaceInvitacion: string;
  /**
   * El reto con el que entro por el enlace del bot, si entro con uno.
   *
   * Lo apunta el BOT al recibir `/start duel_<id>`, porque el boton de Mini
   * App que Telegram ensena despues **no entrega el parametro**. Sin esto el
   * rival abria el juego sin enterarse de que venia a aceptar un reto.
   */
  dueloPendiente?: string | null;
}

/** true si el juego corre fuera de Telegram, es decir en modo desarrollo. */
export const modoDev = !telegram.disponible;

const CLAVE_DEV = 'moonrush.dev.perfil';

/**
 * En modo DEV estan desbloqueados TODOS los trajes.
 *
 * Es lo contrario de lo que hace el servidor, y es lo correcto aqui: en
 * desarrollo hay que poder mirar como queda cada traje sin montarse diez
 * referidos falsos. Como el modo DEV se detecta por la AUSENCIA de Telegram,
 * esto no existe en produccion.
 */
const DESBLOQUEOS_DEV = TRAJES.map((t) => t.requisito).filter(
  (r): r is string => r !== null
);

function perfilDev(): Perfil {
  const vacio: Perfil = {
    record: 0,
    monedas: 0,
    duelosGanados: 0,
    racha: 0,
    avatar: AVATAR_POR_DEFECTO,
    desbloqueos: DESBLOQUEOS_DEV,
    avatarElegido: false,
    // En DEV no hay servidor que lo monte. Es un enlace de mentira, y se
    // nota a proposito: si apareciera en produccion habria que verlo.
    enlaceInvitacion: 'https://t.me/dev/app?startapp=ref_dev',
  };
  try {
    const crudo = localStorage.getItem(CLAVE_DEV);
    if (crudo) {
      const guardado = JSON.parse(crudo) as Partial<Perfil>;
      // Se mezcla con el vacio en vez de devolverlo tal cual: lo que hay en
      // `localStorage` puede ser de una version anterior del juego, sin avatar,
      // y un `undefined` ahi dejaria el menu sin personaje.
      return {
        ...vacio,
        ...guardado,
        avatar: normalizar(guardado.avatar, DESBLOQUEOS_DEV),
        desbloqueos: DESBLOQUEOS_DEV,
      };
    }
  } catch {
    // localStorage puede estar bloqueado (ventana privada). No es grave.
  }
  return vacio;
}

function guardarPerfilDev(p: Perfil): void {
  try {
    localStorage.setItem(CLAVE_DEV, JSON.stringify(p));
  } catch {
    /* igual */
  }
}

/**
 * Una peticion a la API, con el initData en la cabecera.
 *
 * Se exporta para que `anuncios.ts` no tenga que repetir la cabecera ni el
 * manejo de errores: dos sitios que construyen la peticion es un sitio donde
 * uno de los dos se olvidara de firmarla.
 */
export async function pedir<T>(ruta: string, cuerpo?: unknown): Promise<T> {
  const res = await fetch(`/api${ruta}`, {
    method: cuerpo === undefined ? 'GET' : 'POST',
    headers: {
      'Content-Type': 'application/json',
      // La cabecera que el servidor valida. Sin esto, 401.
      'X-Telegram-Init-Data': telegram.initData,
    },
    body: cuerpo === undefined ? null : JSON.stringify(cuerpo),
  });
  if (!res.ok) {
    const texto = await res.text().catch(() => '');
    throw new Error(`${res.status} ${ruta}: ${texto.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

export const api = {
  async perfil(): Promise<Perfil> {
    if (modoDev) return perfilDev();
    const p = await pedir<Perfil>('/me');
    // El avatar que llega del servidor se normaliza igual que el de DEV: el
    // servidor y el cliente leen el MISMO catalogo, asi que normalmente no
    // cambia nada, pero si un dia uno de los dos va por delante del otro, el
    // juego sigue abriendo.
    return { ...p, avatar: normalizar(p.avatar, p.desbloqueos ?? []) };
  },

  /**
   * Guarda el avatar y devuelve el que ha quedado EN EL SERVIDOR.
   *
   * Se devuelve el del servidor y no el que se mando a proposito: si los dos
   * no coinciden, se ve el primer dia en la pantalla y no el dia que un
   * jugador se queja de que su personaje cambia solo.
   */
  async guardarAvatar(avatar: Avatar): Promise<Avatar> {
    if (modoDev) {
      const p = perfilDev();
      const guardado = normalizar(avatar, DESBLOQUEOS_DEV);
      guardarPerfilDev({ ...p, avatar: guardado, avatarElegido: true });
      return guardado;
    }
    const r = await pedir<{ avatar: Avatar; desbloqueos: string[] }>(
      '/me/avatar',
      avatar
    );
    return normalizar(r.avatar, r.desbloqueos ?? []);
  },

  async iniciarPartida(duelId?: string, usarX2 = false): Promise<InicioPartida> {
    if (modoDev) {
      // Un seed al azar, y el runId lleva marca de DEV para que, si algun dia
      // esto llegase al servidor por error, se vea de inmediato en los registros.
      //
      // En modo DEV el x2 se devuelve tal cual se pide, porque aqui no hay
      // servidor que conceda nada. Quien comprueba que el vale existia es
      // `main.ts` antes de llamar: este fichero no sabe de anuncios, y asi no
      // hay un ciclo de importaciones entre los dos.
      const seed = (Math.random() * 0xffffffff) >>> 0;
      return {
        runId: `dev-${Date.now()}`,
        seed,
        firma: 'dev',
        x2: usarX2,
        revivirRestantes: 3,
        precioRevivirMonedas: 150,
      };
    }
    return pedir<InicioPartida>('/run/start', {
      duelId: duelId ?? null,
      usarX2,
    });
  },

  /** Revivir pagando monedas. Concede el SERVIDOR; aqui solo se refleja. */
  async revivirConMonedas(
    runId: string,
    precio: number
  ): Promise<{ concedido: boolean; monedas: number; revivirRestantes: number; motivo?: string }> {
    if (modoDev) {
      // En DEV no hay servidor, pero el boton tiene que poder probarse.
      const p = perfilDev();
      if (p.monedas < precio) {
        return {
          concedido: false,
          monedas: p.monedas,
          revivirRestantes: 3,
          motivo: 'No tienes monedas suficientes.',
        };
      }
      const siguiente = { ...p, monedas: p.monedas - precio };
      guardarPerfilDev(siguiente);
      return { concedido: true, monedas: siguiente.monedas, revivirRestantes: 2 };
    }
    return pedir('/run/revivir-monedas', { runId });
  },

  async terminarPartida(fin: FinPartida): Promise<ResultadoFin> {
    if (modoDev) {
      const p = perfilDev();
      const recordNuevo = fin.distancia > p.record;
      const siguiente: Perfil = {
        ...p,
        record: Math.max(p.record, fin.distancia),
        monedas: p.monedas + fin.monedas,
      };
      guardarPerfilDev(siguiente);
      return {
        aceptada: true,
        monedasAcreditadas: fin.monedas,
        recordNuevo,
        record: siguiente.record,
        monedasTotales: siguiente.monedas,
      };
    }
    return pedir<ResultadoFin>('/run/end', fin);
  },
};

// ---------------------------------------------------------------------------
// Duelos
// ---------------------------------------------------------------------------

export interface Duelo {
  duelId: string;
  seed: number;
  apuesta: number;
  moneda: string;
  estado: string;
  distanciaRetador: number | null;
  distanciaRival: number | null;
  retadorNombre: string;
  rivalNombre: string | null;
  resultado: string | null;
  premio: number;
  soyRetador: boolean;
  expiraEn: string | null;
}

export interface MiembroClan {
  nombre: string;
  record: number;
  soyYo: boolean;
}

export interface ClanMio {
  tiene: boolean;
  id?: number | null;
  titulo?: string;
  miembros?: number;
  total?: number;
  puesto?: number;
  tabla?: MiembroClan[];
}

/**
 * El clan del jugador.
 *
 * Crear uno NO esta aqui a proposito: un clan es un grupo de Telegram, asi que
 * se crea escribiendo `/clan` dentro del grupo. Desde el juego solo se mira y
 * se sale.
 */
export const clan = {
  async mio(): Promise<ClanMio> {
    if (modoDev) {
      // En DEV no hay servidor, pero la pantalla tiene que poder probarse.
      return {
        tiene: true,
        id: 1,
        titulo: 'Clan de desarrollo',
        miembros: 3,
        total: 4200,
        puesto: 2,
        tabla: [
          { nombre: 'Ana', record: 2100, soyYo: false },
          { nombre: 'Tú', record: 1500, soyYo: true },
          { nombre: 'Luis', record: 600, soyYo: false },
        ],
      };
    }
    return pedir<ClanMio>('/clan/mio');
  },

  async salir(): Promise<void> {
    if (modoDev) return;
    await pedir('/clan/salir', {});
  },
};

export const duelos = {
  async crear(apuesta: number, rivalTgId?: number): Promise<Duelo> {
    if (modoDev) {
      // En modo DEV no hay servidor, pero el boton tiene que poder probarse.
      return {
        duelId: `dev-${Date.now()}`,
        seed: (Math.random() * 0xffffffff) >>> 0,
        apuesta,
        moneda: 'monedas',
        estado: 'creado',
        distanciaRetador: null,
        distanciaRival: null,
        retadorNombre: 'DEV',
        rivalNombre: null,
        resultado: null,
        premio: 0,
        soyRetador: true,
        expiraEn: null,
      };
    }
    return pedir<Duelo>('/duel/create', {
      apuesta,
      rivalTgId: rivalTgId ?? null,
    });
  },

  async ver(duelId: string): Promise<Duelo> {
    if (modoDev) throw new Error('sin servidor en modo DEV');
    return pedir<Duelo>(`/duel/${duelId}`);
  },

  async aceptar(duelId: string): Promise<Duelo> {
    if (modoDev) throw new Error('sin servidor en modo DEV');
    return pedir<Duelo>(`/duel/${duelId}/accept`, {});
  },

  /** «Ahora no»: que el servidor olvide el reto con el que entro. */
  async descartar(duelId: string): Promise<void> {
    if (modoDev) return;
    await pedir(`/duel/${duelId}/descartar`, {});
  },

  async revancha(duelId: string): Promise<Duelo> {
    if (modoDev) throw new Error('sin servidor en modo DEV');
    return pedir<Duelo>(`/duel/${duelId}/rematch`, {});
  },

  async mios(): Promise<Duelo[]> {
    if (modoDev) return [];
    const r = await pedir<{ duelos: Duelo[] }>('/duels');
    return r.duelos;
  },
};

// ---------------------------------------------------------------------------
// Referidos
// ---------------------------------------------------------------------------

export interface Hito {
  necesarios: number;
  descripcion: string;
  conseguido: boolean;
}

export interface Referidos {
  enlace: string;
  pendientes: number;
  activados: number;
  monedasGanadas: number;
  fichasGanadas: number;
  multiplicadorPozo: number;
  faltanParaSiguiente: number | null;
  siguienteHito: string | null;
  hitos: Hito[];
  partidasParaActivar: number;
  monedasPorActivacion: number;
}

export const referidos = {
  async mios(): Promise<Referidos> {
    if (modoDev) {
      // En modo DEV no hay servidor, pero la pantalla tiene que poder verse.
      return {
        // Enlace de MENTIRA, y que se note: si este nombre apareciera en
        // produccion habria que mirarlo. El de verdad lo monta el servidor,
        // que es el unico que sabe como se llama el bot.
        enlace: 'https://t.me/BOT_DE_DESARROLLO/app?startapp=ref_000000',
        pendientes: 2,
        activados: 1,
        monedasGanadas: 5000,
        fichasGanadas: 1,
        multiplicadorPozo: 1,
        faltanParaSiguiente: 2,
        siguienteHito: 'Skin de reclutador',
        hitos: [
          { necesarios: 3, descripcion: 'Skin de reclutador', conseguido: false },
          { necesarios: 10, descripcion: 'x1,2 permanente en los puntos de pozo', conseguido: false },
          { necesarios: 50, descripcion: 'x1,5 permanente y la insignia Embajador', conseguido: false },
          { necesarios: 200, descripcion: 'Bonus para todo tu clan', conseguido: false },
        ],
        partidasParaActivar: 10,
        monedasPorActivacion: 5000,
      };
    }
    return pedir<Referidos>('/referrals');
  },
};

/** Pide al servidor la URL firmada de la tarjeta del record, para Stories. */
export async function urlTarjetaRecord(): Promise<string | null> {
  if (modoDev) return null;
  try {
    const r = await pedir<{ url: string }>('/card/record', {});
    return r.url;
  } catch {
    return null;
  }
}
